<template>
  <div class="iv-on-at-start-panel">
    <help-tooltip v-if="ijSubclavianError" type="error" :style="{ 'margin-top': '8px'}">
      {{ ijSubclavianError.message }}
    </help-tooltip>
    <help-tooltip v-if="sameBodyPartError" type="error" :style="{ 'margin-top': '8px'}">
      {{ sameBodyPartError.message }}
    </help-tooltip>

    <div class="tabs-container">
      <vrp-tabs
        :tabs="tabs"
        :active-tab-key="state.activeTabKey"
        @change-tab="setTabKey"
      >
        <template v-slot:header-prefix>
          <md-button
            class="md-secondary-accent md-raised rounded new-initial-iv-button"
            :disabled="addSiteDisabled || readonly"
            @click="startNewInitialIv"
          >
            <md-icon>add</md-icon>
            Site
          </md-button>
        </template>
        <template #item="{ initialIv }">
          <initial-iv
            :initial-iv="initialIv"
            :case-conf="caseConf"
            :readonly="readonly"
            :pending="state.activeTabKey === 'pending'"
            @update="
              updatedInitialIv => updateInitialIv(updatedInitialIv)
            "
          />
        </template>
      </vrp-tabs>
    </div>
    <md-button
      v-if="!removeDisabled"
      class="md-raised md-accent remove-button"
      :disabled="readonly"
      @click="removeInitialIv"
    >
      Remove
    </md-button>
  </div>
</template>

<script>
import get from 'lodash/get';
import Chance from 'chance';

import iv from '~lib/common/config/iv';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import VrpTabs from '../../../common/VrpTabs/VrpTabs';
import InitialIv from './InitialIv.vue';

const chance = new Chance();

export default {
  name: 'iv-on-at-start-panel',
  components: {
    VrpTabs,
    InitialIv,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    navigation: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      state: {
        pendingInitialIv:
          this.caseConf.initialIvs && this.caseConf.initialIvs.length
            ? null
            : {
                id: chance.guid(),
                categoryType: null,
                site: null,
                side: null,
                portAmount: null,
                needle: null,
                salineLock: false,
                isRemovable: false,
              },
        activeTabKey: this.initialActiveId(),
        isLoading: false,
      },
    };
  },
  computed: {
    activeInitialIv() {
      if (this.state.activeTabKey === 'pending') {
        return this.state.pendingInitialIv;
      }
      const index = this.caseConf.initialIvs.findIndex(iiv => iiv.id === this.state.activeTabKey);
      return this.caseConf.initialIvs[index];
    },
    addSiteDisabled() {
      const isPending = !!this.state.pendingInitialIv
      const numSitesAllowed = 10; // ???
      const numSites = (this.caseConf.initialIvs || []).length;
      return isPending || numSites >= numSitesAllowed;
    },
    removeDisabled() {
      const numSites = (this.caseConf.initialIvs || []).length;
      return numSites === 0;
    },
    tabs() {
      const tabs = (this.caseConf.initialIvs || []).map(initIv => {
        return {
          key: initIv.id,
          label: this.initialIvLabel(initIv),
          initialIv: initIv,
        };
      });
      if (this.state.pendingInitialIv) {
        tabs.push({
          key: 'pending',
          label: this.state.pendingInitialIv
              ? this.initialIvLabel(this.state.pendingInitialIv)
              : '',
          initialIv: this.state.pendingInitialIv
        });
      }
      return tabs;
    },
    initialIvErrors() {
      return this.$store
        ? get(
          this.$store.getters['validation/errorsByFieldAndId'],
          'initialIvs.null'
        ) || [] : [];
    },
    ijSubclavianError() {
      return this.initialIvErrors.find(error => error.metadata.type === 'ijSubclavian');
    },
    sameBodyPartError() {
      return this.initialIvErrors.find(error => error.metadata.type === 'sameBodyPart');
    },
  },
  created() {
    if (this.navigation.params && this.navigation.params.field === 'initialIvs') {
      if (this.navigation.params.id) {
        this.setTabKey(this.navigation.params.id);
      }
    }
  },
  methods: {
    setTabKey(key) {
      this.state.activeTabKey = key;
    },
    initialActiveId() {
      if (this.caseConf.initialIvs && this.caseConf.initialIvs.length) {
        return this.caseConf.initialIvs[0].id;
      }
      return 'pending';
    },
    initialIvLabel(initialIv) {
      if (!initialIv.categoryType && !initialIv.site) {
        const index = (this.caseConf.initialIvs || []).indexOf(initialIv);
        const initialIvsCount = (this.caseConf.initialIvs || []).length;
        return `Site ${index !== -1 ? index + 1 : initialIvsCount + 1}`;
      }
      return initialIvLabel(initialIv);
    },
    startNewInitialIv() {
      this.state.pendingInitialIv = {
        id: chance.guid(),
        categoryType: null,
        site: null,
        side: null,
        portAmount: null,
        needle: null,
        salineLock: false,
        isRemovable: false,
      };
      this.state.activeTabKey = 'pending';
    },
    updateInitialIv(updatedInitialIv) {
      if (this.state.isLoading) {
        return;
      }

      let initIvs = this.caseConf.initialIvs || [];
      const index = initIvs.findIndex(iiv => iiv.id === updatedInitialIv.id);

      if (this.state.activeTabKey === 'pending') {
        this.state.pendingInitialIv = updatedInitialIv;
        // Add the pending to the case
        const { pendingInitialIv } = this.state;
        if (pendingInitialIv.site &&
            pendingInitialIv.portAmount &&
            pendingInitialIv.needle &&
            pendingInitialIv.side) {
          initIvs = initIvs.concat(pendingInitialIv);
          this.state.isLoading = true;
          this.$nextTick(() => {
            this.state.activeTabKey = updatedInitialIv.id;
            this.state.pendingInitialIv = null;
            this.state.isLoading = false;
          });
        }
      } else {
        initIvs[index] = updatedInitialIv;
      }

      this.$emit('update', initIvs);
    },
    removeInitialIv() {
      const index = this.caseConf.initialIvs.findIndex(
        iiv => iiv.id === this.activeInitialIv.id
      );
      if (index >= 0) {
        this.caseConf.initialIvs.splice(index, 1);
        if (this.caseConf.initialIvs.length > 0) {
          this.setActiveTab(this.caseConf.initialIvs[0].id);
        } else {
          this.startNewInitialIv();
        }
        this.$emit('update', this.caseConf.initialIvs);
      }
    },
    setActiveTab(id) {
      this.state.activeTabKey = id;
    },
  },
};

export function initialIvLabel(initialIv) {
  let type = iv.categories.find(
    categoryType => categoryType.key === initialIv.categoryType,
  );
  if (!type) {
    type = iv.oasCategories.find(
      categoryType => categoryType.key === initialIv.categoryType,
    );
  }
  const site = iv.sites.find(site => site.key === initialIv.site);
  const abbreviationBySide = {
    LEFT: 'L',
    RIGHT: 'R',
    BOTH: 'C',
  };
  const sideAbbreviation = abbreviationBySide[initialIv.side];
  return `(${sideAbbreviation || ''}) ${type ? type.label.en : ''} ${
    site ? site.label.en : ''
  }`.trim();
}
</script>

<style scoped>
.tabs-container {
  display: flex;
  align-items: center;
  padding-top: 8px;
}

.vrp-tabs {
  flex: 1;
}

.new-initial-iv-button {
  margin: 0;
  padding-right: 5px;
}

.new-initial-iv-button .md-icon {
  color: white;
}
</style>
